/**
 * Claude Code config-dir paths and per-run settings/hook materialization.
 *
 * The PTY harness writes a throwaway `settings.json` (passed via
 * `claude --settings`) wiring two hooks that relay envelopes to a Unix socket:
 *   - PostToolUse (all tools): fire-and-forget tool-progress envelopes.
 *   - PreToolUse (ExitPlanMode only): request/response — the runner's
 *     `canUseTool` verdict (plan/property validation, identification trigger)
 *     is returned as a permissionDecision so the plan-mode exit is gated by
 *     Conveyor instead of the interactive dialog.
 * Writing a per-run file avoids clobbering the user's real `~/.claude/settings.json`.
 */

import { mkdir, writeFile, chmod } from "node:fs/promises";
import { homedir } from "node:os";
import { join } from "node:path";

export function claudeConfigHome(): string {
  return process.env.CLAUDE_CONFIG_DIR ?? join(homedir(), ".claude");
}

export function projectSlug(cwd: string): string {
  return cwd.replace(/\//g, "-");
}

export function sessionTranscriptPath(cwd: string, sessionId: string): string {
  return join(claudeConfigHome(), "projects", projectSlug(cwd), `${sessionId}.jsonl`);
}

/** Where the CLI writes plan-mode plan files (CC 2.x): `<config-home>/plans`. */
export function configHomePlansDir(): string {
  return join(claudeConfigHome(), "plans");
}

/**
 * Per-tool allow rules approximating "allow everything". The CLI rejects a
 * bare `"*"` rule (and an unanchored `"mcp__conveyor"`) as invalid — that
 * parks the TUI on a blocking "Settings Warning" screen at boot — so the
 * built-in tools are named explicitly and the Conveyor MCP tools matched by
 * the only valid glob form (`mcp__<server>__*`). The built-in names matter
 * for the acceptEdits continuation after an auto-mode plan exit (Bash/MCP
 * would otherwise prompt with nobody at the terminal); plan-permission spawns
 * are read-only regardless, and build-capable spawns pass
 * `--dangerously-skip-permissions` and never consult this list.
 */
export const ALLOW_RULES = [
  "Bash",
  "BashOutput",
  "Edit",
  "Write",
  "Read",
  "Glob",
  "Grep",
  "WebFetch",
  "WebSearch",
  "NotebookEdit",
  "Task",
  "TodoWrite",
  "ToolSearch",
  "KillShell",
  "SlashCommand",
  "Skill",
  "mcp__conveyor__*",
];

/**
 * CJS hook helper. Reads the hook payload from stdin and branches on
 * `hook_event_name`:
 *
 *   - PostToolUse: relays a fire-and-forget tool-progress envelope and prints
 *     `{continue:true}` so the CLI proceeds. A short fallback timer guarantees
 *     exit even if the socket is unavailable.
 *   - PreToolUse: writes a `pre_tool_use` request and waits for the runner's
 *     correlated verdict on the same connection, printing a
 *     permissionDecision allow/deny. On socket failure or timeout it fails
 *     CLOSED (deny) — the model retries after fixing the reported problem,
 *     and the runner's denial-escalation backstop bounds pathological loops.
 */
const HOOK_HELPER_SOURCE = `"use strict";
const net = require("node:net");
const crypto = require("node:crypto");

const PRE_TOOL_USE_TIMEOUT_MS = 110000;

let raw = "";
process.stdin.setEncoding("utf8");
process.stdin.on("data", (chunk) => {
  raw += chunk;
});
process.stdin.on("end", () => {
  let payload = {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") payload = parsed;
  } catch {
    payload = {};
  }
  if (payload.hook_event_name === "PreToolUse") {
    preToolUse(payload);
  } else {
    relayProgress(typeof payload.tool_name === "string" ? payload.tool_name : "");
  }
});

function relayProgress(toolName) {
  const socketPath = process.env.CONVEYOR_HOOK_SOCKET;
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    process.stdout.write(JSON.stringify({ continue: true }));
    process.exit(0);
  };
  const fallback = setTimeout(finish, 250);
  if (!socketPath) {
    clearTimeout(fallback);
    finish();
    return;
  }
  const client = net.connect(socketPath, () => {
    // PostToolUse does not supply tool duration, so elapsed_time_seconds is
    // omitted rather than reported as a misleading 0 (the field is optional).
    const line = JSON.stringify({ tool_name: toolName }) + "\\n";
    client.write(line, () => {
      client.end();
    });
  });
  client.on("close", () => {
    clearTimeout(fallback);
    finish();
  });
  client.on("error", () => {
    clearTimeout(fallback);
    finish();
  });
}

function preToolUse(payload) {
  const socketPath = process.env.CONVEYOR_HOOK_SOCKET;
  let done = false;
  const respond = (decision, reason) => {
    if (done) return;
    done = true;
    process.stdout.write(
      JSON.stringify({
        hookSpecificOutput: {
          hookEventName: "PreToolUse",
          permissionDecision: decision,
          ...(reason ? { permissionDecisionReason: reason } : {}),
        },
      }),
    );
    process.exit(0);
  };
  const denyUnavailable = () =>
    respond(
      "deny",
      "Conveyor validation is unavailable right now. Wait a moment and call the tool again.",
    );
  if (!socketPath) {
    denyUnavailable();
    return;
  }
  const id = crypto.randomBytes(8).toString("hex");
  const timer = setTimeout(denyUnavailable, PRE_TOOL_USE_TIMEOUT_MS);
  const client = net.connect(socketPath, () => {
    client.write(
      JSON.stringify({
        kind: "pre_tool_use",
        id,
        tool_name: typeof payload.tool_name === "string" ? payload.tool_name : "",
        tool_input:
          payload.tool_input && typeof payload.tool_input === "object" ? payload.tool_input : {},
      }) + "\\n",
    );
  });
  let buffer = "";
  client.on("data", (chunk) => {
    buffer += chunk.toString("utf8");
    let index = buffer.indexOf("\\n");
    while (index >= 0) {
      const line = buffer.slice(0, index);
      buffer = buffer.slice(index + 1);
      try {
        const verdict = JSON.parse(line);
        if (verdict && verdict.id === id) {
          clearTimeout(timer);
          client.end();
          respond(verdict.decision === "allow" ? "allow" : "deny", verdict.reason);
          return;
        }
      } catch {
        /* keep scanning */
      }
      index = buffer.indexOf("\\n");
    }
  });
  client.on("error", () => {
    clearTimeout(timer);
    denyUnavailable();
  });
  client.on("close", () => {
    clearTimeout(timer);
    denyUnavailable();
  });
}
`;

export interface HookSettingsResult {
  settingsPath: string;
  helperPath: string;
}

export async function writeHookSettings(dir: string): Promise<HookSettingsResult> {
  const helperPath = join(dir, "hook-helper.cjs");
  const settingsPath = join(dir, "settings.json");
  await mkdir(dir, { recursive: true });
  await writeFile(helperPath, HOOK_HELPER_SOURCE, "utf8");
  await chmod(helperPath, 0o755);
  const settings = {
    // Pre-accept Claude Code's "Bypass Permissions mode" disclaimer. Build-capable
    // spawns pass `--dangerously-skip-permissions`; on a real PTY the CLI otherwise
    // parks on the interactive "Yes, I accept / No, exit" dialog whose default focus
    // is "No, exit" — so it cannot be auto-dismissed by an Enter nudge and the run
    // stalls until the auto-nudge watchdog shuts it down. The CLI reads this key
    // from the `--settings` (flagSettings) layer via its bypass-mode gate, so
    // setting it here suppresses the dialog deterministically on every spawn —
    // independent of the `bypassPermissionsModeAccepted` seed in credentials.ts,
    // which the CLI's one-time migration consumes and which is subject to
    // persistence races on the codespace user-home.
    skipDangerousModePermissionPrompt: true,
    // Auto-approve tool calls so the interactive (PTY) agent never stops to
    // prompt the viewer. This only suppresses per-tool approval prompts — it
    // does NOT relax the planning gate: in discovery/plan mode the spawn passes
    // `--permission-mode plan`, which keeps the agent read-only (no edits/commits
    // until it exits plan mode) regardless of this allow-list. In build mode the
    // spawn already bypasses prompts via `--dangerously-skip-permissions`.
    // NOTE: a bare "*" rule is INVALID (the CLI rejects it and parks the TUI on
    // a Settings Warning dialog at boot) — hence the explicit ALLOW_RULES list.
    permissions: {
      allow: ALLOW_RULES,
    },
    hooks: {
      PreToolUse: [
        {
          matcher: "ExitPlanMode",
          hooks: [{ type: "command", command: `node ${JSON.stringify(helperPath)}`, timeout: 120 }],
        },
      ],
      PostToolUse: [
        {
          matcher: "*",
          hooks: [{ type: "command", command: `node ${JSON.stringify(helperPath)}` }],
        },
      ],
    },
  };
  await writeFile(settingsPath, JSON.stringify(settings, null, 2), "utf8");
  return { settingsPath, helperPath };
}
